import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Stethoscope,
  UserRound,
  Activity,
  Pill,
  Award,
  Building2,
  Heart,
  Users,
  ArrowRight,
  Star,
  CheckCircle2,
  MapPin,
  Clock,
  ShieldCheck,
  BookOpen,
} from 'lucide-react';
import { services, doctors, testimonials, whyChooseUs } from '../data/mock';
import { useSeo } from '../hooks/useSeo';

const iconMap = {
  Stethoscope, UserRound, Activity, Pill, Award, Building2, Heart, Users,
};

const HERO_IMAGE = 'https://images.pexels.com/photos/2182972/pexels-photo-2182972.jpeg?auto=compress&cs=tinysrgb&w=1600';

export const Home = () => {
  useSeo({
    title: 'Healthwrx — Multi-Specialty Clinic in Guwahati at Last Gate',
    description: "Multi-specialty clinic at Last Gate, Guwahati (near Beltola & Hatigaon). 11 specialists across 10+ fields. Walk-in or book — confirmed on WhatsApp.",
    canonical: 'https://healthwrx.in/',
  });

  // LCP optimisation: preload the hero image only on the home route, then
  // clean up so other routes don't pay the cost.
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = HERO_IMAGE;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section
        className="relative h-[90vh] min-h-[600px] flex items-center overflow-hidden"
        data-testid="home-hero"
      >
        {/* Real <img> for LCP — fetchpriority + eager loading */}
        <img
          src={HERO_IMAGE}
          alt="Healthwrx Multispeciality Clinic — a modern, welcoming healthcare space in Guwahati"
          width="1600"
          height="900"
          fetchpriority="high"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C5C62]/95 to-[#1C5C62]/70" aria-hidden="true" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Quality Healthcare,<br />
              <span className="text-[#14D1A2]">Accessible to All</span>
            </h1>
            <p className="text-xl text-gray-100 mb-8 leading-relaxed">
              A modern multispeciality clinic at Last Gate, Guwahati — bringing together experienced specialists, careful diagnosis and a calm, patient-first experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-appointment">
                <Button size="lg" className="bg-[#14D1A2] hover:bg-[#10B88A] text-white text-lg px-8 h-14">
                  Book Appointment
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 h-14">
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT / WELCOME — substantive intro for SEO + readability */}
      <section className="py-16 md:py-20 bg-white" data-testid="home-about">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            {/* Copy */}
            <div className="lg:col-span-2 space-y-4 text-gray-700 leading-relaxed">
              <p className="text-sm uppercase tracking-widest text-[#14D1A2] font-semibold">
                Multispeciality care in Guwahati
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                A neighbourhood clinic with hospital-grade expertise.
              </h2>
              <p className="text-base md:text-lg">
                Our clinic is at <strong>Last Gate, on Rajendra Prasad Road</strong>, with <strong>Ganeshguri</strong> and <strong>Beltola</strong> just a few minutes away. We bring together 11 specialists in more than 10 fields, all under one roof. Patients from <strong>Last Gate, Beltola, Six Mile, Khanapara, Ganeshguri, GS Road and Dispur</strong> can reach us in minutes — without a long trip across the city.
              </p>
              <p>
                Walk in for a fever, a flu or a sore throat. Or book a longer slot for a long-term condition. The same team handles both. We work without rush and without jargon. Every visit ends with a clear plan and clear next steps.
              </p>
              <p>
                Our team covers a wide range of care. We see patients for <strong>cardiology, general medicine, pediatrics and urology</strong>. We also offer <strong>orthopedics, dermatology, psychiatry and gastroenterology</strong>. Plus <strong>pulmonology and nephrology</strong>. From a child's cough that won't go to back pain that ruins sleep — there is a specialist here who can help.
              </p>
              <p>
                Behind the scenes, we keep things simple. Our reception team confirms each booking on WhatsApp. Reports stay organised. Follow-ups are easy to schedule. Your records are private and secure.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-3 pt-3">
                <Link
                  to="/symptoms"
                  className="inline-flex items-center gap-2 text-[#14D1A2] font-semibold hover:underline"
                  data-testid="home-about-symptoms-link"
                >
                  <BookOpen className="w-4 h-4" />
                  Browse 34 patient symptom guides
                </Link>
                <Link
                  to="/doctors"
                  className="inline-flex items-center gap-2 text-[#14D1A2] font-semibold hover:underline"
                  data-testid="home-about-doctors-link"
                >
                  <UserRound className="w-4 h-4" />
                  Meet our doctors
                </Link>
              </div>
            </div>

            {/* Trust facts column */}
            <aside className="lg:col-span-1 bg-gray-50 rounded-2xl p-6 space-y-5 border border-gray-100" data-testid="home-trust-card">
              <Fact icon={Users} label="Specialists" value="11+" />
              <Fact icon={Stethoscope} label="Specialty fields" value="10+" />
              <Fact icon={BookOpen} label="Patient guides" value="34" />
              <Fact icon={ShieldCheck} label="Doctor-reviewed content" value="Every page" />
              <div className="pt-3 border-t border-gray-200 space-y-2 text-sm text-gray-600">
                <p className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#14D1A2]" />
                  <span>
                    5th Floor, S.B. Plaza,<br />Rajendra Prasad Road, Last Gate,<br />Guwahati 781006
                    <br />
                    <a
                      href="https://share.google/qNd3DAEdwcdrcygx4"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-1 text-[#14D1A2] hover:underline text-xs font-medium"
                      data-testid="home-gbp-link"
                    >
                      View on Google Maps →
                    </a>
                  </span>
                </p>
                <p className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 text-[#14D1A2]" />
                  <span>Mon – Sat · 9:00 AM – 7:00 PM</span>
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50" data-testid="home-services">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From everyday consultations to long-term specialist care, our team supports you through every stage of life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-0 bg-white hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#14D1A2]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#14D1A2] transition-colors">
                      <Icon className="w-7 h-7 text-[#14D1A2] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-[#14D1A2] text-[#14D1A2] hover:bg-[#14D1A2] hover:text-white">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white" data-testid="home-why-choose">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why choose our clinic</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The small things matter — short waits, clear advice, and doctors who take the time to explain.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <div key={item.id} className="text-center">
                  <div className="w-16 h-16 bg-[#14D1A2]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-[#14D1A2]" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Doctors */}
      <section className="py-20 bg-gray-50" data-testid="home-featured-doctors">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Doctors</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experienced specialists who treat every visit as the only visit that matters.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {doctors.slice(0, 3).map((doctor) => (
              <Card key={doctor.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={doctor.image}
                    alt={`${doctor.name}, ${doctor.specialization} at Healthwrx Guwahati`}
                    width="500"
                    height="500"
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{doctor.name}</h3>
                  <p className="text-[#14D1A2] font-medium mb-2">{doctor.specialization}</p>
                  <p className="text-gray-600 text-sm mb-3">{doctor.education}</p>
                  <div className="flex items-center text-gray-500 text-sm">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-[#14D1A2]" />
                    {doctor.experience} experience
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/doctors">
              <Button size="lg" variant="outline" className="border-[#14D1A2] text-[#14D1A2] hover:bg-[#14D1A2] hover:text-white">
                View All Doctors
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white relative" data-testid="home-testimonials">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/8460220/pexels-photo-8460220.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
          aria-hidden="true"
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Patients Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real stories from people we've had the privilege to care for.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#14D1A2] text-[#14D1A2]" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1C5C62] text-white" data-testid="home-cta">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to see a specialist?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Book online in under a minute, or send us a quick WhatsApp — we'll handle the rest.
          </p>
          <Link to="/book-appointment">
            <Button size="lg" className="bg-[#14D1A2] hover:bg-[#10B88A] text-white text-lg px-10 h-14" data-testid="home-cta-book-button">
              Book Your Appointment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <span className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-[#14D1A2] shrink-0">
      <Icon className="w-5 h-5" />
    </span>
    <span>
      <span className="block text-xl font-bold text-gray-900 leading-none">{value}</span>
      <span className="block text-xs uppercase tracking-wider text-gray-500 mt-1">{label}</span>
    </span>
  </div>
);
